/**
 * Per-case Medical Match panel state that has to survive a reload. Mirrors the
 * pattern used by fletch's `transcribingFilesStorage`: every read and write is
 * total, so an unavailable `localStorage` (private browsing, blocked origin)
 * leaves the panel working, just without remembering anything.
 */
import {
  LIVE_PROCESSING_QUEUE_STORAGE_PREFIX,
  LIVE_QUESTION_NOTICE_LIMIT,
  LIVE_QUESTION_NOTICES_STORAGE_PREFIX,
} from './constants';

/** A live question that failed, kept so its notice survives a reload. */
export interface FailedLiveQuestion {
  questionId: string;
  question: string;
}

/**
 * Failures are held here because the check that finds them only looks back to
 * the queue's previous change, so it finds each one once: without remembering
 * them, switching sidebar tab or reloading would lose the notice for good.
 * Stalled questions need no such record — they sit in the processing list
 * indefinitely, so the queue hands them back on every poll.
 */
export interface LiveQuestionNoticeState {
  /** Failures still to be shown, oldest first. */
  failedQuestions: FailedLiveQuestion[];
  /** Question ids the user has cleared; their notice never comes back. */
  dismissedQuestionIds: string[];
}

export const EMPTY_LIVE_QUESTION_NOTICE_STATE: LiveQuestionNoticeState = {
  failedQuestions: [],
  dismissedQuestionIds: [],
};

const liveQuestionNoticesKey = (caseId: string) => `${LIVE_QUESTION_NOTICES_STORAGE_PREFIX}${caseId}`;

const liveProcessingQueueKey = (caseId: string) => `${LIVE_PROCESSING_QUEUE_STORAGE_PREFIX}${caseId}`;

const isFailedLiveQuestion = (value: unknown): value is FailedLiveQuestion =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as FailedLiveQuestion).questionId === 'string' &&
  typeof (value as FailedLiveQuestion).question === 'string';

const parseLiveQuestionNotices = (value: unknown): LiveQuestionNoticeState => {
  if (typeof value !== 'object' || value === null) return EMPTY_LIVE_QUESTION_NOTICE_STATE;

  const { failedQuestions, dismissedQuestionIds } = value as Partial<LiveQuestionNoticeState>;
  return {
    failedQuestions: Array.isArray(failedQuestions) ? failedQuestions.filter(isFailedLiveQuestion) : [],
    dismissedQuestionIds: Array.isArray(dismissedQuestionIds)
      ? dismissedQuestionIds.filter((id): id is string => typeof id === 'string')
      : [],
  };
};

/** Notices outstanding for this case, and the ones already cleared. */
export const readLiveQuestionNotices = (caseId: string): LiveQuestionNoticeState => {
  try {
    const raw = localStorage.getItem(liveQuestionNoticesKey(caseId));
    if (!raw) return EMPTY_LIVE_QUESTION_NOTICE_STATE;

    return parseLiveQuestionNotices(JSON.parse(raw));
  } catch {
    return EMPTY_LIVE_QUESTION_NOTICE_STATE;
  }
};

/**
 * Trims both lists to the newest `LIVE_QUESTION_NOTICE_LIMIT` entries, persists
 * that, and hands it back — so what the panel holds is what a reload would read,
 * rather than the two drifting apart once the cap bites.
 */
export const writeLiveQuestionNotices = (caseId: string, notices: LiveQuestionNoticeState): LiveQuestionNoticeState => {
  const trimmed: LiveQuestionNoticeState = {
    failedQuestions: notices.failedQuestions.slice(-LIVE_QUESTION_NOTICE_LIMIT),
    dismissedQuestionIds: notices.dismissedQuestionIds.slice(-LIVE_QUESTION_NOTICE_LIMIT),
  };

  try {
    const key = liveQuestionNoticesKey(caseId);
    // Nothing outstanding and nothing cleared is the default state, so it's
    // dropped rather than stored — otherwise every case the user opens leaves a
    // key behind.
    if (trimmed.failedQuestions.length === 0 && trimmed.dismissedQuestionIds.length === 0) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, JSON.stringify(trimmed));
    }
  } catch {
    // See `readLiveQuestionNotices`.
  }

  return trimmed;
};

/** The live-processing queue as we last acted on it. */
export interface LiveProcessingQueueRecord {
  questionIds: string[];
  /**
   * The last time, as `Date.now()`, a poll saw exactly these questions queued.
   * Whatever has left the queue since did so after this.
   */
  recordedAt: number;
}

/**
 * The live-processing queue as we last acted on it. `null` means no baseline,
 * which reads as "first look at this case".
 */
export const readLiveProcessingQueue = (caseId: string): LiveProcessingQueueRecord | null => {
  try {
    const raw = localStorage.getItem(liveProcessingQueueKey(caseId));
    if (raw === null) return null;

    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;

    const { questionIds, recordedAt } = parsed as Partial<LiveProcessingQueueRecord>;
    if (!Array.isArray(questionIds) || !questionIds.every((id) => typeof id === 'string')) return null;
    if (typeof recordedAt !== 'number' || !Number.isFinite(recordedAt)) return null;
    return { questionIds, recordedAt };
  } catch {
    return null;
  }
};

export const writeLiveProcessingQueue = (caseId: string, questionIds: string[]): void => {
  try {
    const key = liveProcessingQueueKey(caseId);
    // An empty queue is the resting state and reads the same as no baseline, so
    // it's dropped rather than left behind on every case ever opened.
    if (questionIds.length === 0) {
      localStorage.removeItem(key);
      return;
    }
    const record: LiveProcessingQueueRecord = { questionIds, recordedAt: Date.now() };
    localStorage.setItem(key, JSON.stringify(record));
  } catch {
    // See `readLiveProcessingQueue`.
  }
};
